// src/pages/operations/Transactions.jsx
//
// Financial ledger for the selected organization. Data comes from the real
// backend: GET /api/audit-logs/?org_id= returns one entry per expense and
// contribution, each tagged with a `kind` discriminator. Expenses carry a
// `status` (PENDING / APPROVED / REJECTED); contributions have no status, so
// the status badge is only rendered for expense entries.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// 'DD/MM/YYYY' -> 'YYYY-MM-DD' so date inputs can be compared lexically.
const toIso = (ddmmyyyy) => {
  const [d, m, y] = String(ddmmyyyy).split('/');
  return d && m && y ? `${y}-${m}-${d}` : '';
};

const STATUS_STYLES = {
  PENDING:  { className: 'bg-amber-100 text-amber-800', key: 'pending' },
  APPROVED: { className: 'bg-green-100 text-green-800', key: 'approved' },
  REJECTED: { className: 'bg-red-100 text-red-800', key: 'rejected' },
};

const Transactions = ({ ngo, onBack, onHome, onEditProfile }) => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('transactions');
  const { t: tc } = useTranslation('common');

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [type, setType] = useState('');

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);
  
    // Close the language menu on outside click.
    useEffect(() => {
      if (!langMenuOpen) return;
      const handleClickOutside = (e) => {
        if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
          setLangMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [langMenuOpen]);
  

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  useEffect(() => {
    if (!ngo?.id) return;

    let cancelled = false;

    const fetchTransactions = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiCall(
          `/api/audit-logs/?org_id=${ngo.id}`,
          {},
          getAccessTokenSilently
        );
        if (!cancelled) {
          setTransactions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) setError(tc('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTransactions();

    return () => {
      cancelled = true;
    };
  }, [ngo, getAccessTokenSilently, tc]);

  const filtered = useMemo(() => {
    let results = transactions;
    if (search) {
      results = results.filter(t =>
        t.description.toLowerCase().includes(search.toLowerCase()));
    }
    if (type) results = results.filter(t => t.type === type);
    if (fromDate) {
      results = results.filter(t => toIso(t.date) >= fromDate);
    }
    if (toDate) {
      results = results.filter(t => toIso(t.date) <= toDate);
    }
    return results;
  }, [transactions, search, type, fromDate, toDate]);

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setType('');
    setSearch('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3 relative">
        <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />
        
        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Search */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{tc('search')}</p>
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg px-3.5 py-3.5 mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">{t('filters')}</p>

          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('fromDate')}</p>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="w-full bg-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('toDate')}</p>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="w-full bg-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('type')}</p>
            <div className="flex gap-2">
              {['credit', 'debit', ''].map((t, i) => (
                <button
                  key={i}
                  onClick={() => setType(t)}
                  className={`flex-1 rounded-lg py-2 text-xs font-medium transition ${
                    type === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t === '' ? tc('all') : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              // Filtering is already reactive via the `filtered` useMemo above,
              // so Apply doesn't need to do anything itself — it's here for
              // the expected UX affordance. If you'd rather filters only take
              // effect on click, swap the `filtered` useMemo's deps for a
              // separate "applied filters" state set here.
              onClick={() => {}}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('apply')}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('reset')}
            </button>
          </div>
        </div>

        {/* Transaction list */}
        <div className="flex flex-col gap-2.5">
          {loading && (
            <p className="text-center text-sm text-gray-400">{t('loading')}</p>
          )}
          {!loading && error && (
            <p className="text-center text-sm text-red-500">{error}</p>
          )}
          {!loading && !error && filtered.length === 0 && (
            <p className="text-center text-sm text-gray-400">{t('noData')}</p>
          )}
          {!loading && !error && filtered.map(tx => {
            const statusStyle = tx.kind === 'expense' ? STATUS_STYLES[tx.status] : null;
            return (
              <div key={tx.id} className="bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{tx.description}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{tx.date}</p>
                  {statusStyle && (
                    <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusStyle.className}`}>
                      {t(statusStyle.key)}
                    </span>
                  )}
                </div>
                <p className={`text-sm font-bold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {tx.type === 'credit' ? '+' : '-'}₹{tx.amount}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Transactions;